/**
 * Bittersweet Herb Farm - Cart Management
 * Handles all cart operations using Shopify Ajax Cart API
 */

class CartManager {
  constructor() {
    this.cartIcon = document.querySelector('.cart-btn');
    this.cartCount = document.querySelectorAll('[data-cart-count]');
    this.init();
  }

  init() {
    this.setupAddToCartButtons();
    this.setupQuantityControls();
    this.setupRemoveButtons();
    this.updateCartCount();
  }

  /**
   * Setup Add to Cart buttons throughout the site
   */
  setupAddToCartButtons() {
    const addToCartButtons = document.querySelectorAll('[data-add-to-cart]');
    
    addToCartButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const variantId = button.dataset.variantId || button.dataset.productId;
        const quantity = parseInt(button.dataset.quantity) || 1;
        
        if (!variantId) {
          console.error('No variant ID found');
          return;
        }
        
        await this.addToCart(variantId, quantity, button);
      });
    });
  }

  /**
   * Add item to cart
   */
  async addToCart(variantId, quantity = 1, button = null) {
    // Show loading state
    if (button) {
      button.disabled = true;
      const originalText = button.innerHTML;
      button.innerHTML = `
        <span style="
          display: inline-block;
          width: 18px;
          height: 18px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        "></span>
        <span>Adding...</span>
      `;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const item = await response.json();
      
      // Update cart count
      await this.updateCartCount();
      
      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { item, action: 'add' }
      }));
      
      // Show success notification
      if (window.BittersweetHerbFarm && window.BittersweetHerbFarm.utils) {
        window.BittersweetHerbFarm.utils.showNotification(
          `${item.product_title} added to cart!`,
          'success'
        );
      }
      
      // Reset button
      if (button) {
        button.disabled = false;
        button.innerHTML = `
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="9" cy="21" r="1"></circle>
            <circle cx="20" cy="21" r="1"></circle>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
          </svg>
          <span>Add to Cart</span>
        `;
      }
      
      return item;
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      
      // Show error notification
      if (window.BittersweetHerbFarm && window.BittersweetHerbFarm.utils) {
        window.BittersweetHerbFarm.utils.showNotification(
          'Error adding to cart. Please try again.',
          'error'
        );
      }
      
      // Reset button
      if (button) {
        button.disabled = false;
        button.innerHTML = `
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="9" cy="21" r="1"></circle>
            <circle cx="20" cy="21" r="1"></circle>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
          </svg>
          <span>Add to Cart</span>
        `;
      }
      
      throw error;
    }
  }

  /**
   * Update cart item quantity
   */
  async updateQuantity(lineKey, quantity) {
    try {
      // Show loading overlay
      this.showLoadingState(true);
      
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      const cart = await response.json();
      
      // Update cart count
      await this.updateCartCount();
      
      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart, action: 'update' }
      }));
      
      // Reload page to show updated cart with slight delay for UX
      setTimeout(() => {
        window.location.reload();
      }, 300);
      
      return cart;
      
    } catch (error) {
      console.error('Error updating cart:', error);
      this.showLoadingState(false);
      
      this.showNotification(
        'Error updating cart. Please try again.',
        'error'
      );
      
      throw error;
    }
  }

  /**
   * Show/hide loading state overlay
   */
  showLoadingState(show) {
    let overlay = document.getElementById('cart-loading-overlay');
    
    if (show && !overlay) {
      overlay = document.createElement('div');
      overlay.id = 'cart-loading-overlay';
      overlay.innerHTML = `
        <div style="
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          animation: fadeIn 0.2s ease-out;
        ">
          <div style="
            background: white;
            padding: 2rem;
            border-radius: 1rem;
            box-shadow: 0 8px 32px rgba(107, 127, 106, 0.2);
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
          ">
            <div style="
              width: 48px;
              height: 48px;
              border: 3px solid rgba(107, 127, 106, 0.2);
              border-top-color: var(--sage-700);
              border-radius: 50%;
              animation: spin 0.8s linear infinite;
            "></div>
            <p style="
              font-size: 1rem;
              color: var(--sage-700);
              font-weight: 500;
            ">Updating cart...</p>
          </div>
        </div>
      `;
      document.body.appendChild(overlay);
    } else if (!show && overlay) {
      overlay.remove();
    }
  }

  /**
   * Show notification message
   */
  showNotification(message, type = 'info') {
    if (window.BittersweetHerbFarm && window.BittersweetHerbFarm.utils) {
      window.BittersweetHerbFarm.utils.showNotification(message, type);
    } else {
      // Fallback notification
      alert(message);
    }
  }

  /**
   * Show custom confirmation dialog
   */
  async showConfirmation(message, confirmText = 'Yes', cancelText = 'Cancel') {
    return new Promise((resolve) => {
      // Create modal overlay
      const modal = document.createElement('div');
      modal.className = 'cart-confirmation-modal';
      modal.innerHTML = `
        <div class="modal-overlay">
          <div class="modal-content">
            <p class="modal-message">${message}</p>
            <div class="modal-buttons">
              <button class="cancel-btn">${cancelText}</button>
              <button class="confirm-btn">${confirmText}</button>
            </div>
          </div>
        </div>
      `;
      
      document.body.appendChild(modal);
      
      // Handle button clicks
      const cancelBtn = modal.querySelector('.cancel-btn');
      const confirmBtn = modal.querySelector('.confirm-btn');
      
      cancelBtn.addEventListener('click', () => {
        modal.remove();
        resolve(false);
      });
      
      confirmBtn.addEventListener('click', () => {
        modal.remove();
        resolve(true);
      });
      
      // Close on overlay click
      modal.querySelector('.modal-overlay').addEventListener('click', (e) => {
        if (e.target.classList.contains('modal-overlay')) {
          modal.remove();
          resolve(false);
        }
      });
    });
  }

  /**
   * Remove item from cart
   */
  async removeItem(lineKey) {
    return await this.updateQuantity(lineKey, 0);
  }

  /**
   * Get current cart
   */
  async getCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  /**
   * Update cart count in header
   */
  async updateCartCount() {
    const cart = await this.getCart();
    
    if (cart) {
      this.cartCount.forEach(element => {
        const oldCount = parseInt(element.getAttribute('data-count')) || 0;
        const newCount = cart.item_count;
        const count = newCount > 99 ? '99+' : newCount;
        
        element.textContent = count;
        element.setAttribute('data-count', newCount);
        
        // Show/hide count badge with animation
        if (newCount > 0) {
          element.style.display = 'flex';
          
          // Add pop animation if count increased
          if (newCount > oldCount) {
            element.style.animation = 'none';
            // Force reflow to restart animation
            void element.offsetWidth;
            element.style.animation = 'cartBubblePop 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)';
          }
        } else {
          element.style.display = 'none';
        }
      });
    }
  }

  /**
   * Setup quantity +/- controls on cart page
   */
  setupQuantityControls() {
    const minusButtons = document.querySelectorAll('[data-quantity-minus]');
    const plusButtons = document.querySelectorAll('[data-quantity-plus]');
    const quantityInputs = document.querySelectorAll('input[name="updates[]"]');
    
    console.log(`Setting up quantity controls: ${minusButtons.length} minus, ${plusButtons.length} plus, ${quantityInputs.length} inputs`);
    
    // Minus buttons
    minusButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        
        // Disable button to prevent double clicks
        if (button.disabled) return;
        
        const line = button.dataset.line;
        const input = document.querySelector(`input[data-line="${line}"]`);
        
        if (input) {
          const currentQty = parseInt(input.value) || 0;
          const newQty = Math.max(0, currentQty - 1);
          
          if (newQty === 0) {
            // Removing item - show custom confirmation
            const confirmRemove = await this.showConfirmation(
              'Remove this item from your cart?',
              'Remove',
              'Cancel'
            );
            if (confirmRemove) {
              const lineKey = input.id.replace('updates_', '');
              this.removeItem(lineKey);
            }
          } else {
            // Update quantity
            input.value = newQty;
            const lineKey = input.id.replace('updates_', '');
            this.updateQuantity(lineKey, newQty);
          }
        }
      });
    });

    // Plus buttons
    plusButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Disable button to prevent double clicks
        if (button.disabled) return;
        
        const line = button.dataset.line;
        const input = document.querySelector(`input[data-line="${line}"]`);
        
        if (input) {
          const currentQty = parseInt(input.value) || 0;
          const newQty = currentQty + 1;
          input.value = newQty;
          
          const lineKey = input.id.replace('updates_', '');
          this.updateQuantity(lineKey, newQty);
        }
      });
    });

    // Direct input changes
    quantityInputs.forEach(input => {
      let timeout;
      
      // Handle change event
      input.addEventListener('change', async (e) => {
        clearTimeout(timeout);
        timeout = setTimeout(async () => {
          const lineKey = input.id.replace('updates_', '');
          let newQty = parseInt(input.value) || 0;
          
          // Ensure positive number
          if (newQty < 0) {
            newQty = 0;
            input.value = 0;
          }
          
          if (newQty === 0) {
            const confirmRemove = await this.showConfirmation(
              'Remove this item from your cart?',
              'Remove',
              'Cancel'
            );
            if (confirmRemove) {
              this.removeItem(lineKey);
            } else {
              // Reset to 1 if user cancels
              input.value = 1;
            }
          } else {
            this.updateQuantity(lineKey, newQty);
          }
        }, 800);
      });
      
      // Prevent invalid input
      input.addEventListener('keypress', (e) => {
        // Only allow numbers
        if (e.key && !/[0-9]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace') {
          e.preventDefault();
        }
      });
    });
  }

  /**
   * Setup remove item buttons
   */
  setupRemoveButtons() {
    const removeButtons = document.querySelectorAll('[data-remove-item]');
    console.log(`Setting up remove buttons: ${removeButtons.length} found`);
    
    removeButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Prevent double clicks
        if (button.disabled) return;
        
        const confirmRemove = await this.showConfirmation(
          'Remove this item from your cart?',
          'Remove',
          'Cancel'
        );
        
        if (confirmRemove) {
          const lineKey = button.dataset.removeItem;
          
          // Disable button and show loading state
          button.disabled = true;
          button.style.opacity = '0.5';
          button.style.cursor = 'not-allowed';
          
          this.removeItem(lineKey).catch(() => {
            // Re-enable button on error
            button.disabled = false;
            button.style.opacity = '0.7';
            button.style.cursor = 'pointer';
          });
        }
      });
    });
  }
}

// Add animations and modal styles
const cartStyle = document.createElement('style');
cartStyle.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes cartBubblePop {
    0% { transform: scale(1); }
    50% { transform: scale(1.3); }
    100% { transform: scale(1); }
  }
  
  /* Cart Confirmation Modal Styles */
  .cart-confirmation-modal .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10001;
    animation: fadeIn 0.2s ease-out;
    padding: 1rem;
  }
  
  .cart-confirmation-modal .modal-content {
    background: white;
    padding: 2rem;
    border-radius: var(--radius-xl);
    box-shadow: var(--shadow-xl);
    max-width: 440px;
    width: 100%;
  }
  
  .cart-confirmation-modal .modal-message {
    font-size: 1.125rem;
    color: var(--sage-900);
    margin: 0 0 1.5rem 0;
    line-height: 1.6;
    text-align: center;
  }
  
  .cart-confirmation-modal .modal-buttons {
    display: flex;
    gap: 0.75rem;
    justify-content: stretch;
  }
  
  .cart-confirmation-modal .modal-buttons button {
    flex: 1;
    padding: 1rem 1.5rem;
    border-radius: var(--radius-lg);
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all var(--duration-base);
    border: none;
    min-height: 52px;
  }
  
  .cart-confirmation-modal .cancel-btn {
    background: white;
    color: var(--sage-700);
    border: 2px solid var(--sage-300);
    order: 1;
  }
  
  .cart-confirmation-modal .cancel-btn:hover {
    background: var(--sage-50);
    border-color: var(--sage-400);
  }
  
  .cart-confirmation-modal .cancel-btn:active {
    transform: scale(0.98);
  }
  
  .cart-confirmation-modal .confirm-btn {
    background: var(--caramel-600);
    color: white;
    box-shadow: 0 2px 8px rgba(184, 128, 79, 0.25);
    order: 2;
  }
  
  .cart-confirmation-modal .confirm-btn:hover {
    background: var(--caramel-700);
    box-shadow: 0 4px 12px rgba(184, 128, 79, 0.35);
  }
  
  .cart-confirmation-modal .confirm-btn:active {
    transform: scale(0.98);
  }
  
  /* Mobile Layout - Stack Buttons Vertically */
  @media (max-width: 640px) {
    .cart-confirmation-modal .modal-content {
      padding: 1.5rem;
    }
    
    .cart-confirmation-modal .modal-message {
      font-size: 1rem;
      margin-bottom: 1.25rem;
    }
    
    .cart-confirmation-modal .modal-buttons {
      flex-direction: column-reverse;
      gap: 0.75rem;
    }
    
    .cart-confirmation-modal .modal-buttons button {
      width: 100%;
      padding: 1rem;
      font-size: 1rem;
    }
    
    .cart-confirmation-modal .confirm-btn {
      order: 1;
    }
    
    .cart-confirmation-modal .cancel-btn {
      order: 2;
    }
  }
  
  /* Tablet Layout */
  @media (min-width: 641px) and (max-width: 1024px) {
    .cart-confirmation-modal .modal-content {
      max-width: 400px;
    }
  }
`;
document.head.appendChild(cartStyle);

// Initialize cart manager immediately
// Force initialization regardless of ready state
(function() {
  console.log('Initializing CartManager...');
  window.BittersweetHerbFarm = window.BittersweetHerbFarm || {};
  window.BittersweetHerbFarm.cart = new CartManager();
  console.log('CartManager initialized successfully');
})();

// Export for use in other scripts
window.CartManager = CartManager;
